/**
 * The cart. Genuine client state — it exists only on this device until it is submitted — so it
 * is a Redux slice and not an RTK Query cache entry.
 *
 * The idempotency key is generated the moment the first line lands, not when Complete is tapped.
 * That is the difference between a retry being safe and a retry ringing the sale up twice: the
 * key has to predate the network, because the network is what fails.
 */

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import {
  uuidv7,
  type BrokerId,
  type CatalogueVariant,
  type CustomerId,
  type PriceType,
  type SaleId,
  type UnitId,
  type VariantId,
} from '@pos/shared';

export type CartLine = {
  /** Variant id, plus the unit id for a serialized line — two IMEIs are two lines. */
  key: string;
  variantId: VariantId;
  displayName: string;
  sku: string;
  isSerialized: boolean;
  quantity: number;
  /** What will be charged. Overridable within the floor, which the server enforces. */
  unitPriceKes: number;
  retailKes: number;
  wholesaleKes: number | null;
  wholesaleMinQty: number | null;
  retailFloorKes: number | null;
  wholesaleFloorKes: number | null;
  inventoryUnitId: UnitId | null;
  serialNo: string | null;
  /** Cached on-hand at the time the line was added, for the "only 2 left" warning. */
  availableQuantity: number;
  /** True once the salesperson types a price, so a tier change stops overwriting it. */
  priceOverridden: boolean;
};

/**
 * A line with no catalogue variant behind it — a misc. item, a service charge, anything worth
 * selling that was never worth adding to the catalogue. Kept as its own array rather than folded
 * into `CartLine` because everything a catalogue line carries — stock warnings, a floor, a
 * wholesale tier, an IMEI — simply does not apply here, and a `CartLine` with those fields
 * meaningless-but-present would be a worse shape than two arrays.
 */
export type GenericCartLine = {
  key: string;
  description: string;
  quantity: number;
  unitPriceKes: number;
};

export type SubmissionState = 'idle' | 'submitting' | 'failed';

export type CartState = {
  lines: CartLine[];
  genericLines: GenericCartLine[];
  priceType: PriceType;
  customerName: string | null;
  customerPhone: string | null;
  /**
   * Set only when the salesperson actually picked a registered customer — most sales have none,
   * and that is the correct default. `customerName`/`customerPhone` still work independently of
   * this: a walk-in's name typed for the receipt does not require a customer record to exist.
   */
  customerId: CustomerId | null;
  /** Set only when a broker brought in this sale. Most sales have none. */
  brokerId: BrokerId | null;
  /** Kept alongside the id purely so the cart can show a name without a lookup. */
  brokerName: string | null;
  note: string | null;
  idempotencyKey: string;
  submission: SubmissionState;
  lastError: string | null;

  /**
   * Set once the sale has been rung up and its stock has moved.
   *
   * From this moment the cart is no longer a proposal — it is a completed sale that has not been
   * paid for yet, which is a real and legitimate state in phase 3. It exists so that taking a
   * second tender, or retrying after a failed M-Pesa push, attaches money to the sale that
   * already exists rather than ringing up a second one.
   */
  saleId: SaleId | null;
};

const fresh = (): CartState => ({
  lines: [],
  genericLines: [],
  priceType: 'retail',
  customerName: null,
  customerPhone: null,
  customerId: null,
  brokerId: null,
  brokerName: null,
  note: null,
  idempotencyKey: uuidv7(),
  submission: 'idle',
  lastError: null,
  saleId: null,
});

/**
 * Which price applies. Wholesale only takes over once the line reaches its tier — a "wholesale"
 * toggle that charged the wholesale rate for one unit would be a pricing bug the customer
 * notices before anyone else does.
 */
export function priceFor(
  line: Pick<CartLine, 'retailKes' | 'wholesaleKes' | 'wholesaleMinQty' | 'quantity'>,
  type: PriceType,
): number {
  if (type !== 'wholesale' || line.wholesaleKes === null) return line.retailKes;
  return line.quantity >= (line.wholesaleMinQty ?? 1) ? line.wholesaleKes : line.retailKes;
}

/**
 * The lowest price this line goes at without a manager, for whichever tier `priceFor` would
 * actually charge — same tier-selection rule, so the two never disagree. The floor when one is
 * set; the price itself when none is, which is the rule the server applies (owner's decision after
 * a case listed at 850 went out at 500 with nothing asked).
 */
export function floorFor(
  line: Pick<
    CartLine,
    'retailKes' | 'wholesaleKes' | 'retailFloorKes' | 'wholesaleFloorKes' | 'wholesaleMinQty' | 'quantity'
  >,
  type: PriceType,
): number {
  const wholesaleApplies =
    type === 'wholesale' && line.wholesaleKes !== null && line.quantity >= (line.wholesaleMinQty ?? 1);
  return wholesaleApplies
    ? (line.wholesaleFloorKes ?? line.wholesaleKes ?? line.retailKes)
    : (line.retailFloorKes ?? line.retailKes);
}

const cartSlice = createSlice({
  name: 'cart',
  initialState: fresh(),
  reducers: {
    lineAdded: {
      reducer(
        state,
        action: PayloadAction<{
          variant: CatalogueVariant;
          unit: { id: UnitId; serialNo: string } | null;
        }>,
      ) {
        const { variant, unit } = action.payload;
        const key = unit === null ? variant.id : `${variant.id}:${unit.id}`;

        const existing = state.lines.find((line) => line.key === key);

        // A serialized line is one handset. Tapping the same IMEI twice is a mis-tap, not a
        // quantity of two, so it is ignored rather than silently doubling the sale.
        if (existing !== undefined) {
          if (unit === null) {
            existing.quantity += 1;
            if (!existing.priceOverridden)
              existing.unitPriceKes = priceFor(existing, state.priceType);
          }
          return;
        }

        const line: CartLine = {
          key,
          variantId: variant.id,
          displayName: variant.displayName,
          sku: variant.sku,
          isSerialized: variant.trackingMode === 'serialized',
          quantity: 1,
          unitPriceKes: variant.retailKes,
          retailKes: variant.retailKes,
          wholesaleKes: variant.wholesaleKes ?? null,
          wholesaleMinQty: variant.wholesaleMinQty ?? null,
          retailFloorKes: variant.retailFloorKes ?? null,
          wholesaleFloorKes: variant.wholesaleFloorKes ?? null,
          inventoryUnitId: unit?.id ?? null,
          serialNo: unit?.serialNo ?? null,
          availableQuantity: variant.quantity,
          priceOverridden: false,
        };

        line.unitPriceKes = priceFor(line, state.priceType);
        state.lines.push(line);
      },
      prepare: (
        variant: CatalogueVariant,
        unit: { id: UnitId; serialNo: string } | null = null,
      ) => ({
        payload: { variant, unit },
      }),
    },

    /** A cart saved on the device, put back after a reload. See `cartStorage`. */
    cartRestored(_state, action: PayloadAction<CartState>) {
      return action.payload;
    },

    quantityChanged(state, action: PayloadAction<{ key: string; quantity: number }>) {
      const line = state.lines.find((entry) => entry.key === action.payload.key);
      if (line === undefined || line.isSerialized) return;

      line.quantity = Math.max(1, Math.trunc(action.payload.quantity));
      if (!line.priceOverridden) line.unitPriceKes = priceFor(line, state.priceType);
    },

    priceOverridden(state, action: PayloadAction<{ key: string; unitPriceKes: number }>) {
      const line = state.lines.find((entry) => entry.key === action.payload.key);
      if (line === undefined) return;

      line.unitPriceKes = Math.max(0, Math.trunc(action.payload.unitPriceKes));
      line.priceOverridden = true;
    },

    lineRemoved(state, action: PayloadAction<string>) {
      state.lines = state.lines.filter((line) => line.key !== action.payload);
    },

    genericLineAdded: {
      reducer(state, action: PayloadAction<GenericCartLine>) {
        state.genericLines.push(action.payload);
      },
      prepare: (description: string, quantity: number, unitPriceKes: number) => ({
        payload: { key: uuidv7(), description, quantity, unitPriceKes },
      }),
    },

    genericLineRemoved(state, action: PayloadAction<string>) {
      state.genericLines = state.genericLines.filter((line) => line.key !== action.payload);
    },

    priceTypeChanged(state, action: PayloadAction<PriceType>) {
      state.priceType = action.payload;

      // An overridden price survives the toggle. The salesperson typed it on purpose.
      for (const line of state.lines) {
        if (!line.priceOverridden) line.unitPriceKes = priceFor(line, action.payload);
      }
    },

    customerChanged(state, action: PayloadAction<{ name: string | null; phone: string | null }>) {
      state.customerName = action.payload.name;
      state.customerPhone = action.payload.phone;
    },

    /** Picking a registered customer also fills the receipt's name/phone from their own record. */
    customerAttached(
      state,
      action: PayloadAction<{ id: CustomerId; name: string; phone: string | null }>,
    ) {
      state.customerId = action.payload.id;
      state.customerName = action.payload.name;
      state.customerPhone = action.payload.phone;
    },

    /** Detaches the customer record. The typed name/phone go with it — they were set as a unit. */
    customerDetached(state) {
      state.customerId = null;
      state.customerName = null;
      state.customerPhone = null;
    },

    brokerAttached(state, action: PayloadAction<{ id: BrokerId; name: string }>) {
      state.brokerId = action.payload.id;
      state.brokerName = action.payload.name;
    },

    brokerDetached(state) {
      state.brokerId = null;
      state.brokerName = null;
    },

    noteChanged(state, action: PayloadAction<string | null>) {
      state.note = action.payload;
    },

    submissionStarted(state) {
      state.submission = 'submitting';
      state.lastError = null;
    },

    /**
     * The cart survives a failed submission, with the same idempotency key. The salesperson
     * taps Complete again and the server either finishes the original sale or rejects it for the
     * same reason — never creates a second one.
     */
    submissionFailed(state, action: PayloadAction<string>) {
      state.submission = 'failed';
      state.lastError = action.payload;
    },

    /** Cleared only on success. A new key is minted for the next customer. */
    /**
     * The sale exists and its stock has moved. The cart stays exactly as it is — the lines are
     * still what the receipt will say — but nothing may change them from here.
     */
    saleRungUp(state, action: PayloadAction<SaleId>) {
      state.saleId = action.payload;
      state.submission = 'idle';
      state.lastError = null;
    },

    cartCleared() {
      return fresh();
    },
  },
});

export const {
  cartRestored,
  lineAdded,
  quantityChanged,
  priceOverridden,
  lineRemoved,
  genericLineAdded,
  genericLineRemoved,
  priceTypeChanged,
  customerChanged,
  customerAttached,
  customerDetached,
  brokerAttached,
  brokerDetached,
  noteChanged,
  submissionStarted,
  submissionFailed,
  saleRungUp,
  cartCleared,
} = cartSlice.actions;

export const cartReducer = cartSlice.reducer;
